import {challengeService} from '../services/challenge.service';
import {ToolbarComponent} from '../components/toolbar.component';
import {ButtonComponent} from '../components/button.component';
import {AnalyticsUtil, ANALYTICS_EVENTS} from '../utils/analytics.util';
import {router} from '../utils/router.util';
import {
  calculateTotalPrize,
  calculateTotalSupportedMedias,
  calculateTotalWatchedMedias,
  campaignCache,
  getBenefitUnit
} from '../utils/campaign.util';
import {AACampaign, AAMedia, AAMediaTypes} from '@akross/artemis-ads-sdk';
import {AAMediaPlayResult} from '@akross/artemis-ads-sdk';
import {ArtemisRewardPage} from './artemis-reward.page';
import {ROUTES} from '../constants/routes.constants';
import {checkCarrotAndExecute} from '../utils/carrot.util';
import {logger} from '../utils/logger.util';

export class MediaCampaignDetailPage {
  private container: HTMLElement;
  private campaign: AACampaign | null = null;
  private zoneId: string = 'fb458184-caad-49ed-ba56-ef47a2b491b6';

  constructor(container: HTMLElement) {
    this.container = container;
  }

  async render(campaignId: string): Promise<void> {

    try {
      // Tentar buscar do cache primeiro (dados já carregados na home)
      this.campaign = campaignCache.getCampaign(campaignId);

      // Se não encontrou no cache, buscar da API (fallback)
      if (!this.campaign) {
        logger.debug('[MediaCampaignDetailPage] Campanha não encontrada no cache, buscando da API...');
        this.campaign = await challengeService.getCampaignDetail(campaignId, this.zoneId);
      } else {
        logger.debug('[MediaCampaignDetailPage] Campanha encontrada no cache, reutilizando dados...');
      }

      if (!this.campaign) {
        logger.warnWithContext('MediaCampaignDetailPage', 'Campanha não encontrada (nem no cache nem na API), campaignId:', campaignId);
        this.renderError('Campanha não encontrada');
        return;
      }

      this.renderContent();
      this.attachEvents();

      AnalyticsUtil.track(ANALYTICS_EVENTS.CHALLENGE_DETAIL_VIEWED, {
        campaignId: campaignId
      });
    } catch (error: unknown) {
      logger.error('Error loading Artemis campaign detail:', error);
      const errorMessage = error instanceof Error ? error.message : 'Tente novamente mais tarde';
      this.renderError('Erro ao carregar desafio', errorMessage);
    }
  }

  private renderError(title: string, message?: string): void {
    this.container.innerHTML = this.buildErrorHTML(title, message);
    this.attachEvents();
  }

  private buildErrorHTML(title: string, message?: string): string {
    return `
      <div class="challenge-detail-page">
        ${ToolbarComponent.render({
          showBackButton: true,
          showMenuButton: false,
          elevation: 2,
          logoImage: '/vivo-ads-logo.svg',
          logoAlt: 'Vivo Ads'
        })}
        <div class="campaigns-state">
          <div class="error-screen">
            <h2>Ops! Algo deu errado</h2>
            <p>${message || title}</p>
            <div id="error-button-container"></div>
          </div>
        </div>
      </div>
    `;
  }

  private renderContent(): void {
    if (!this.campaign) return;

    const title = this.campaign.mainData?.layout?.title || this.campaign.campaignName || '';
    const totalPrize = calculateTotalPrize(this.campaign);
    const unit = getBenefitUnit(this.campaign) || 'MB';
    const totalWatchedMedias = calculateTotalWatchedMedias(this.campaign);
    const totalSupportedMedias = calculateTotalSupportedMedias(this.campaign);

    // Calcular progresso baseado no total de mídias suportadas
    const progressPercentage = totalSupportedMedias > 0
      ? (totalWatchedMedias / totalSupportedMedias) * 100
      : 0;

    // Buscar a primeira mídia disponível
    const firstAvailableMedia = this.campaign.mainData.media.find((media: AAMedia) => !media.viewed);
    const mediaThumbnail = firstAvailableMedia?.thumbnail || '';

    this.container.innerHTML = `
      <div class="challenge-detail-page">
        ${ToolbarComponent.render({
          title: title,
          showBackButton: true,
          showMenuButton: false,
          elevation: 2,
          logoImage: '/vivo-ads-logo.svg',
          logoAlt: 'Vivo Ads'
        })}
        <div class="challenge-detail-scroll">
          <div class="challenge-detail-content">
            <!-- Seção de pontos e missão -->
            <div class="artemis-reward-section">
              <div class="artemis-reward-points">
                <span class="artemis-reward-number">${totalPrize}</span>
                <span class="artemis-reward-label">${unit}</span>
              </div>
              <div class="artemis-mission-card">
                <svg class="artemis-info-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                  <path d="M12 16V12M12 8H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
                <div class="artemis-mission-content">
                  <div class="artemis-mission-title">Assista todos os anúncios</div>
                  <div class="artemis-mission-description">Cumpra essa missão por completo pra garantir todos os pontos</div>
                </div>
              </div>
            </div>

            <!-- Barra de progresso -->
            <div class="artemis-progress-section">
              <div class="artemis-progress-bar">
                <div class="artemis-progress-fill" style="width: ${progressPercentage}%"></div>
              </div>
              <div class="artemis-progress-text">
                <span class="artemis-progress-number">${totalWatchedMedias}</span>
                <span class="artemis-progress-label"> de ${totalSupportedMedias} de anúncios vistos</span>
              </div>
            </div>

            <!-- Player de vídeo -->
            <div class="artemis-video-player ${firstAvailableMedia ? 'available' : 'no-media'}">
              ${mediaThumbnail ? `
                <div class="artemis-video-thumbnail" style="background-image: url('${mediaThumbnail}')"></div>
              ` : `
                <div class="artemis-video-placeholder"></div>
              `}
              <div class="artemis-video-overlay">
                <svg class="artemis-video-play-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                  <path d="M10 8L16 12L10 16V8Z" fill="currentColor"/>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }



  private attachEvents(): void {
    // Função para voltar - sempre voltar para home
    const goBack = () => {
      router.navigate(ROUTES.HOME);
    };

    ToolbarComponent.attachEvents(this.container, {
      showBackButton: true,
      showMenuButton: false,
      backButtonAction: goBack
    });

    // Botão de voltar na tela de erro (usando ButtonComponent para manter consistência)
    const errorButtonContainer = document.getElementById('error-button-container');
    if (errorButtonContainer) {
      const backButton = ButtonComponent.createPrimaryButton({
        text: 'Voltar',
        id: 'error-back-button',
        fullWidth: true,
        onClick: goBack
      });
      errorButtonContainer.appendChild(backButton);
    }

    // Clique no player de vídeo
    const videoPlayer = this.container.querySelector('.artemis-video-player.available');
    if (videoPlayer) {
      videoPlayer.addEventListener('click', async () => {
        await this.onMediaClick();
      });
    }
  }

  private async onMediaClick(): Promise<void> {
    logger.debug('onMediaClick - iniciado');

    if (!this.campaign) {
      logger.warnWithContext('MediaCampaignDetailPage', 'onMediaClick - campaign is null');
      return;
    }

    // Buscar a primeira mídia disponível (viewed = false) diretamente do objeto campaign
    const media = this.campaign.mainData.media.find((m: AAMedia) => !m.viewed);
    if (!media) {
      logger.warnWithContext('MediaCampaignDetailPage', 'onMediaClick - nenhuma mídia disponível encontrada');
      return;
    }

    logger.debug('onMediaClick - mídia encontrada:', media.uuid);

    try {
      AnalyticsUtil.track(ANALYTICS_EVENTS.CHALLENGE_MEDIA_CLICKED, {
        campaignId: this.campaign.campaignUuid,
        mediaId: media.uuid
      });

      // Verificar pacote cenoura apenas se o tipo da mídia for programatica_v2
      const isProgramaticaV2 = media.type === AAMediaTypes.PROGRAMMATIC_V2;

      if (isProgramaticaV2) {
        // Verificar pacote cenoura antes de executar a ação
        await checkCarrotAndExecute(async () => {
          logger.debug('[ArtemisMediasDetailPage] executeMediaClick - chamando click() da instância Artemis');
          var mediaResult = await media.click();
          console.log('current campaign', this.campaign);
          this.handleMediaResult(mediaResult, media);
          console.log('current campaign after', this.campaign);
        });
      } else {
        // Executar diretamente sem verificar pacote cenoura
        logger.debug('[ArtemisMediasDetailPage] executeMediaClick - chamando click() da instância Artemis');
        var mediaResult = await media.click();
        this.handleMediaResult(mediaResult, media);
      }
    } catch (error: unknown) {
      logger.error('onMediaClick - erro geral:', error);
    }
  }

  /**
   * Processa o resultado da mídia - verifica se foi MEDIA_WATCHED ou REWARDED e atualiza o estado
   */
  private handleMediaResult(result: any, media: AAMedia): void {
    logger.debug('handleMediaResult - processando resultado');

    // Verificar se o resultado indica que a mídia foi assistida
    // Pode vir como enum, string, ou através de propriedades diferentes
    const resultValue = result?.result || result?.playResult || result?.status;

    // Verificar se foi REWARDED (recompensa ganha)
    const isRewarded = resultValue === AAMediaPlayResult.REWARDED;

    // Verificar se foi MEDIA_WATCHED (mídia assistida, mas sem recompensa ainda)
    const isWatched = resultValue === AAMediaPlayResult.MEDIA_WATCHED;

    // Se foi REWARDED, navegar para página de sucesso
    if (isRewarded) {
      logger.debug('Mídia recompensada! Navegando para página de sucesso...');

      // Todos os IDs necessários (campaignId, requestId, trackingId, mediaId) já estão dentro do objeto media
      ArtemisRewardPage.openPage(media);

      return;
    }

    // Se foi apenas assistida (sem recompensa), atualizar estado normalmente
    if (isWatched) {
      logger.debug('Mídia assistida! Atualizando estado...');

      // Atualizar a mídia diretamente no objeto campaign usando o UUID
      // Isso garante que estamos atualizando o objeto correto dentro de this.campaign
      if (this.campaign) {
        const campaignMedia = this.campaign.mainData.media.find((m: AAMedia) => m.uuid === media.uuid);
        if (campaignMedia) {
          campaignMedia.viewed = true;
          logger.debug('Mídia marcada como visualizada no objeto campaign:', campaignMedia.uuid);
        } else {
          logger.warnWithContext('MediaCampaignDetailPage', 'Mídia não encontrada no campaign para atualizar:', media.uuid);
          // Fallback: atualizar o objeto media passado como parâmetro
          media.viewed = true;
        }

        // Decrementar mediasLeft em todas as mídias (lógica do app)
        this.campaign.mainData.media.forEach((m: AAMedia) => {
          if (m.mediasLeft != null) {
            m.mediasLeft = m.mediasLeft - 1;
          }
        });

        // Atualizar cache com a campanha modificada
        campaignCache.updateCampaign(this.campaign);
      }

      // Atualizar UI em tempo real
      this.updateProgress();
    } else {
      logger.debug('Mídia não foi assistida ou resultado diferente');
    }
  }

  /**
   * Atualiza a barra de progresso e contadores em tempo real
   */
  private updateProgress(): void {
    if (!this.campaign) {
      logger.warnWithContext('MediaCampaignDetailPage', 'updateProgress: campaign is null');
      return;
    }

    const totalWatchedMedias = calculateTotalWatchedMedias(this.campaign);
    const totalSupportedMedias = calculateTotalSupportedMedias(this.campaign);

    const progressPercentage = totalSupportedMedias > 0
      ? (totalWatchedMedias / totalSupportedMedias) * 100
      : 0;

    logger.debug('updateProgress - atualizando progresso:', progressPercentage);

    // Atualizar número destacado
    const progressNumber = this.container.querySelector('.artemis-progress-number');
    if (progressNumber) {
      progressNumber.textContent = `${totalWatchedMedias}`;
    }

    // Atualizar texto do label
    const progressLabel = this.container.querySelector('.artemis-progress-label');
    if (progressLabel) {
      progressLabel.textContent = ` de ${totalSupportedMedias} de anúncios vistos`;
    }

    // Atualizar barra de progresso
    const progressFill = this.container.querySelector('.artemis-progress-fill') as HTMLElement;
    if (progressFill) {
      progressFill.style.width = `${progressPercentage}%`;
    } else {
      logger.warnWithContext('MediaCampaignDetailPage', 'Barra de progresso não encontrada');
    }

    // Se não há mais mídias disponíveis, atualizar o player
    const firstAvailableMedia = this.campaign.mainData.media.find((m: AAMedia) => !m.viewed);
    const videoPlayer = this.container.querySelector('.artemis-video-player');

    if (videoPlayer) {
      if (!firstAvailableMedia) {
        // Não há mais mídias disponíveis
        videoPlayer.classList.remove('available');
        videoPlayer.classList.add('no-media');
      } else {
        // Há mídia disponível - atualizar thumbnail se necessário
        const thumbnail = videoPlayer.querySelector('.artemis-video-thumbnail') as HTMLElement;
        if (thumbnail && firstAvailableMedia.thumbnail) {
          thumbnail.style.backgroundImage = `url('${firstAvailableMedia.thumbnail}')`;
        }
        // Garantir que está marcado como available
        videoPlayer.classList.remove('no-media');
        videoPlayer.classList.add('available');
      }
    }
  }

}

